import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import Modal from '../ui/Modal';
import { Button, Input, Select, Badge } from '../ui/Kit';
import Spinner from '../ui/Spinner';
import { useToast } from '../ui/Toast';
import { useAuth } from '../../contexts/AuthContext';
import { api, supabase } from '../../lib/api';
import {
  activityDateFromScheduledAt,
  buildAppointmentBookedNotes,
} from '../../lib/appointmentActivityNotes';
import { getActiveDirectoryUsers, useUsersDirectory } from '../../lib/usersDirectory';

const APPOINTMENT_TYPES_BY_CHANNEL = {
  LD: [
    { value: 'intro_call', label: 'Intro Call' },
    { value: 'needs_analysis', label: 'Needs Analysis' },
    { value: 'policy_review', label: 'Policy Review' },
    { value: 'closing_call', label: 'Closing Call' },
  ],
  IM: [
    { value: 'discovery_call', label: 'Discovery Call' },
    { value: 'business_presentation', label: 'Business Presentation' },
    { value: 'follow_up', label: 'Follow-Up' },
    { value: 'onboarding', label: 'Onboarding' },
  ],
  AM: [
    { value: 'intro_call', label: 'Intro Call' },
    { value: 'strategy_session', label: 'Strategy Session' },
    { value: 'proposal_review', label: 'Proposal Review' },
    { value: 'closing_call', label: 'Closing Call' },
  ],
};

function getAppointmentTypesForChannel(channel) {
  return APPOINTMENT_TYPES_BY_CHANNEL[channel] || APPOINTMENT_TYPES_BY_CHANNEL.LD;
}

function buildAutoTitle(typeLabel, lead) {
  const firstName = lead?.first_name?.trim() || '';
  const lastName = lead?.last_name?.trim() || '';
  const name = `${firstName} ${lastName}`.trim() || 'Lead';
  return `${typeLabel} with ${name}`;
}

const SMS_CONFIRMATION_TEMPLATES = {
  en: 'Hi {firstName}, your appointment with {agentName} is confirmed for {date} at {time}. Reply STOP to opt out.',
  es: 'Hola {firstName}, tu cita con {agentName} está confirmada para el {date} a las {time}. Responde STOP para cancelar.',
};

function buildConfirmationSmsMessage(language, { firstName, agentName, date, time }) {
  const template = SMS_CONFIRMATION_TEMPLATES[language === 'es' ? 'es' : 'en'];
  return template
    .replace('{firstName}', firstName)
    .replace('{agentName}', agentName)
    .replace('{date}', date)
    .replace('{time}', time);
}

function formatConfirmationDate(ymd, language) {
  if (!ymd) return '';
  const [Y, M, D] = ymd.split('-').map((x) => parseInt(x, 10));
  if (!Number.isFinite(Y) || !Number.isFinite(M) || !Number.isFinite(D)) return '';
  const d = new Date(Y, M - 1, D);
  if (language === 'es') {
    return format(d, "EEEE, d 'de' MMM", { locale: es }).replace(/\./g, '');
  }
  return format(d, 'EEEE, MMM d');
}

const DURATION_OPTIONS = [15, 30, 45, 60].map((m) => ({ value: String(m), label: `${m} min` }));

const MEETING_TYPE_OPTIONS = [
  { value: 'zoom', label: 'Zoom' },
  { value: 'custom_link', label: 'Custom Link' },
  { value: 'in_person', label: 'In Person' },
];

const DEFAULT_OFFICE_ADDRESS = '632 Newark Avenue, Jersey City NJ 07306. Suite 212.';

function resolveCustomLinkDefault(defaultCustomLink, userEmail) {
  const link = String(defaultCustomLink ?? '').trim();
  if (link) return link;
  const email = String(userEmail ?? '').trim();
  return email ? `mailto:${email}` : '';
}

function resolveInPersonDefault(officeAddress) {
  const addr = String(officeAddress ?? '').trim();
  return addr || DEFAULT_OFFICE_ADDRESS;
}

function buildCalendarLocationPayload(meetingType, { zoomJoinUrl, meetingLocation }) {
  const trimmedLoc = (meetingLocation || '').trim();
  if (meetingType === 'zoom') return (zoomJoinUrl || '').trim() || trimmedLoc || undefined;
  if ((meetingType === 'custom_link' || meetingType === 'in_person') && trimmedLoc) {
    return trimmedLoc;
  }
  return undefined;
}

const DAY_ORDER = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const DEFAULT_AVAILABILITY = () => ({
  monday: { enabled: true, start: '09:00', end: '18:00' },
  tuesday: { enabled: true, start: '09:00', end: '18:00' },
  wednesday: { enabled: true, start: '09:00', end: '18:00' },
  thursday: { enabled: true, start: '09:00', end: '18:00' },
  friday: { enabled: true, start: '09:00', end: '18:00' },
  saturday: { enabled: false, start: '09:00', end: '13:00' },
  sunday: { enabled: false, start: '09:00', end: '13:00' },
});

function normalizeAvailability(raw) {
  const base = DEFAULT_AVAILABILITY();
  if (!raw || typeof raw !== 'object') return base;
  for (const key of Object.keys(base)) {
    const d = raw[key];
    if (d && typeof d === 'object') {
      base[key] = {
        enabled: Boolean(d.enabled),
        start: typeof d.start === 'string' ? d.start : base[key].start,
        end: typeof d.end === 'string' ? d.end : base[key].end,
      };
    }
  }
  return base;
}

function parseHm(hm) {
  if (!hm || typeof hm !== 'string') return 0;
  const [h, m] = hm.split(':').map((x) => parseInt(x, 10));
  return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
}

/** Civil clock in IANA zone → UTC instant (iterative). */
function utcInstantForWallTime(ymd, hour, minute, timeZone) {
  const [Y, M, D] = ymd.split('-').map((x) => parseInt(x, 10));
  if (!Number.isFinite(Y) || !Number.isFinite(M) || !Number.isFinite(D)) {
    return new Date();
  }
  let t = Date.UTC(Y, M - 1, D, hour, minute, 0, 0);
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  for (let i = 0; i < 60; i++) {
    const parts = fmt.formatToParts(new Date(t)).reduce((acc, p) => {
      if (p.type !== 'literal') acc[p.type] = Number(p.value);
      return acc;
    }, {});
    if (
      parts.year === Y &&
      parts.month === M &&
      parts.day === D &&
      parts.hour === hour &&
      parts.minute === minute
    ) {
      return new Date(t);
    }
    t += ((hour - parts.hour) * 60 + (minute - parts.minute)) * 60 * 1000;
  }
  return new Date(Date.UTC(Y, M - 1, D, hour, minute, 0, 0));
}

function weekdayKeyInTz(ymd, timeZone) {
  const [Y, M, D] = ymd.split('-').map((x) => parseInt(x, 10));
  const noon = Date.UTC(Y, M - 1, D, 12, 0, 0, 0);
  const wd = new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'long' })
    .format(new Date(noon))
    .toLowerCase();
  return wd;
}

function formatReminderLabel(minutes) {
  const m = Number(minutes) || 60;
  if (m >= 1440) {
    const d = m / 1440;
    return d === 1 ? '24 hours before' : `${d} days before`;
  }
  if (m >= 60) {
    const h = m / 60;
    return h === 1 ? '1 hour before' : `${h} hours before`;
  }
  return `${m} minutes before`;
}

function roleBadgeVariant(role) {
  const r = String(role || '').toLowerCase();
  if (r === 'admin' || r === 'super_admin') return 'purple';
  if (r === 'manager') return 'primary';
  if (r === 'setter' || r === 'associate') return 'warning';
  if (r === 'closer') return 'success';
  return 'default';
}

function roleLabel(role) {
  const r = String(role || '').toLowerCase();
  if (!r) return 'Agent';
  return r.replace(/_/g, ' ');
}

function intervalsOverlap(a0, a1, b0, b1) {
  return a0 < b1 && a1 > b0;
}

function applyTemplate(template, vars) {
  if (!template) return '';
  return template.replace(/\{\{(\w+)\}\}/g, (_, key) => vars[key] ?? `{{${key}}}`);
}

function todayYmdLocal() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export default function BookAppointmentModal({
  isOpen,
  onClose,
  lead,
  onSuccess,
  existingAppointment = null,
  initialDate = null,
  initialSlotStart = null,
}) {
  const { addToast } = useToast();
  const { user: authUser } = useAuth();
  const { users: directoryUsers, loading: directoryLoading } = useUsersDirectory();
  const [me, setMe] = useState(null);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [agentSearch, setAgentSearch] = useState('');
  const [agentDropdownOpen, setAgentDropdownOpen] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [agentSnap, setAgentSnap] = useState(null);
  const [busySlots, setBusySlots] = useState([]);
  const [busyLoading, setBusyLoading] = useState(false);

  const agents = useMemo(() => getActiveDirectoryUsers(directoryUsers), [directoryUsers]);
  const agentsReady = !directoryLoading;

  const [title, setTitle] = useState('');
  const [appointmentType, setAppointmentType] = useState('intro_call');
  const [meetingType, setMeetingType] = useState('custom_link');
  const [meetingLocation, setMeetingLocation] = useState('');
  const [defaultCustomLink, setDefaultCustomLink] = useState('');
  const [agentZoomConnected, setAgentZoomConnected] = useState(false);
  const [agentZoomLoading, setAgentZoomLoading] = useState(false);
  const [notes, setNotes] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlotStart, setSelectedSlotStart] = useState(null);
  const [durationMinutes, setDurationMinutes] = useState('30');
  const [sendConfirmationSms, setSendConfirmationSms] = useState(true);
  const [confirmationBody, setConfirmationBody] = useState('');
  const [smsLanguage, setSmsLanguage] = useState('en');
  const [sendReminderSms, setSendReminderSms] = useState(true);
  const [saving, setSaving] = useState(false);
  const [guests, setGuests] = useState([]);
  const [guestUserSearch, setGuestUserSearch] = useState('');
  const [guestUserDropdownOpen, setGuestUserDropdownOpen] = useState(false);
  const [externalGuestFormOpen, setExternalGuestFormOpen] = useState(false);
  const [externalGuestName, setExternalGuestName] = useState('');
  const [externalGuestPhone, setExternalGuestPhone] = useState('');
  const [externalGuestEmail, setExternalGuestEmail] = useState('');
  const agentSnapReq = useRef(0);
  const formInitializedRef = useRef(false);
  const prevSelectedAgentRef = useRef(null);
  const prevZoomCheckAgentRef = useRef(null);
  const titleWasManuallyEdited = useRef(false);
  const lastAutoTitleRef = useRef('');

  const timeZone = agentSnap?.timezone || 'America/New_York';
  const availability = useMemo(() => normalizeAvailability(agentSnap?.availability), [agentSnap]);
  const appointmentTypeOptions = useMemo(
    () => getAppointmentTypesForChannel(lead?.channel),
    [lead?.channel],
  );

  const initializeFormFields = useCallback(
    (currentUser, filtered, customLinkDefault = '') => {
      if (existingAppointment) {
        setTitle(existingAppointment.title || '');
        setAppointmentType(existingAppointment.appointment_type || 'intro_call');
        setMeetingType(existingAppointment.meeting_type || 'custom_link');
        setMeetingLocation(existingAppointment.meeting_location || '');
        setNotes(existingAppointment.notes || '');
        const st = existingAppointment.scheduled_at;
        if (st) {
          const d = parseISO(st);
          const ymd = format(d, 'yyyy-MM-dd');
          setSelectedDate(ymd);
          setSelectedSlotStart(st);
        } else {
          setSelectedDate(todayYmdLocal());
          setSelectedSlotStart(null);
        }
        setDurationMinutes(String(existingAppointment.duration_minutes ?? 30));
        setSendConfirmationSms(false);
        setSendReminderSms(true);
        setSelectedAgentId(existingAppointment.assigned_to || '');
      } else {
        const types = getAppointmentTypesForChannel(lead?.channel);
        const firstType = types[0];
        const autoTitle = buildAutoTitle(firstType?.label || 'Intro Call', lead);
        titleWasManuallyEdited.current = false;
        lastAutoTitleRef.current = autoTitle;
        setTitle(autoTitle);
        setAppointmentType(firstType?.value || 'intro_call');
        setMeetingType('custom_link');
        setMeetingLocation(resolveCustomLinkDefault(customLinkDefault, currentUser?.email));
        setNotes('');
        setSelectedDate(initialDate || todayYmdLocal());
        setSelectedSlotStart(initialSlotStart || null);
        setDurationMinutes('30');
        setSendConfirmationSms(Boolean(lead?.phone?.trim()));
        setSendReminderSms(true);
        const defaultAgent = filtered.find((u) => u.id === currentUser?.id);
        setSelectedAgentId(defaultAgent?.id || filtered[0]?.id || '');
      }
      setSmsLanguage(lead?.language === 'es' ? 'es' : 'en');
      setGuests([]);
      setGuestUserSearch('');
      setGuestUserDropdownOpen(false);
      setExternalGuestFormOpen(false);
      setExternalGuestName('');
      setExternalGuestPhone('');
      setExternalGuestEmail('');
      setAgentSearch('');
      setAgentDropdownOpen(false);
      setBusySlots([]);
    },
    [existingAppointment, lead?.channel, lead?.first_name, lead?.last_name, lead?.language, lead?.phone, initialDate, initialSlotStart],
  );

  useEffect(() => {
    if (!isOpen) {
      formInitializedRef.current = false;
      prevSelectedAgentRef.current = null;
      prevZoomCheckAgentRef.current = null;
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !lead?.agency_id) return;

    let cancelled = false;
    (async () => {
      setAgentsLoading(true);
      try {
        const [currentUser, calSettings] = await Promise.all([
          api.auth.getCurrentUser(),
          api.agentCalendarSettings.get().catch(() => null),
        ]);
        if (cancelled) return;
        setMe(currentUser);
        const linkDefault = calSettings?.default_custom_link ?? '';
        setDefaultCustomLink(linkDefault);
      } catch (e) {
        if (!cancelled) addToast({ type: 'error', title: 'Could not load booking form', message: e.message });
      } finally {
        if (!cancelled) setAgentsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, lead?.agency_id, addToast]);

  // Initialize form once me + directory roster are ready
  useEffect(() => {
    if (!isOpen || formInitializedRef.current || !agentsReady || !me) return;
    formInitializedRef.current = true;
    initializeFormFields(me, agents, defaultCustomLink);
  }, [isOpen, agentsReady, agents, me, defaultCustomLink, initializeFormFields]);

  useEffect(() => {
    if (!isOpen || !selectedAgentId) {
      setAgentSnap(null);
      if (!isOpen) prevSelectedAgentRef.current = null;
      return;
    }
    const agentChanged = prevSelectedAgentRef.current !== selectedAgentId;
    if (agentChanged) {
      prevSelectedAgentRef.current = selectedAgentId;
    }
    const req = ++agentSnapReq.current;
    let cancelled = false;
    api.agentCalendarSettings
      .getBookingSnapshot(selectedAgentId)
      .then((snap) => {
        if (cancelled || req !== agentSnapReq.current) return;
        setAgentSnap(snap);
        if (!existingAppointment && agentChanged) {
          setDurationMinutes(String(snap?.default_duration_minutes ?? 30));
        }
      })
      .catch(() => {
        if (cancelled || req !== agentSnapReq.current) return;
        setAgentSnap(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, selectedAgentId, existingAppointment]);

  useEffect(() => {
    if (!isOpen || !selectedAgentId) {
      setAgentZoomConnected(false);
      setAgentZoomLoading(false);
      return;
    }

    const agentChanged = prevZoomCheckAgentRef.current !== selectedAgentId;
    prevZoomCheckAgentRef.current = selectedAgentId;

    let cancelled = false;
    setAgentZoomLoading(true);
    api.agentCalendarSettings
      .getForUser(selectedAgentId)
      .then((settings) => {
        if (cancelled) return;
        const connected = Boolean(settings?.zoom_connected);
        setAgentZoomConnected(connected);
        if (typeof settings?.default_custom_link === 'string') {
          setDefaultCustomLink(settings.default_custom_link);
        }
        if (agentChanged) {
          if (existingAppointment) {
            setMeetingType((prev) => (prev === 'zoom' && !connected ? 'custom_link' : prev));
          } else {
            setMeetingType(connected ? 'zoom' : 'custom_link');
            if (connected) setMeetingLocation('');
            else {
              setMeetingLocation(
                resolveCustomLinkDefault(
                  settings?.default_custom_link ?? '',
                  agents.find((u) => u.id === selectedAgentId)?.email || me?.email,
                ),
              );
            }
          }
        }
      })
      .catch(() => {
        if (cancelled) return;
        setAgentZoomConnected(false);
        if (agentChanged && !existingAppointment) {
          setMeetingType('custom_link');
        }
      })
      .finally(() => {
        if (!cancelled) setAgentZoomLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, selectedAgentId, existingAppointment, agents, me]);

  useEffect(() => {
    if (!isOpen || !selectedAgentId || !selectedDate) {
      setBusySlots([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setBusyLoading(true);
      try {
        const snap = await api.agentCalendarSettings.getBookingSnapshot(selectedAgentId);
        if (cancelled) return;
        const tz = snap?.timezone || 'America/New_York';
        const dayStart = utcInstantForWallTime(selectedDate, 0, 0, tz).toISOString();
        const dayEnd = new Date(utcInstantForWallTime(selectedDate, 23, 59, tz).getTime() + 59 * 1000 + 999).toISOString();
        if (!snap?.google_connected) {
          setBusySlots([]);
          return;
        }
        const busy = await api.googleCalendar.getAvailability(selectedAgentId, dayStart, dayEnd);
        if (!cancelled) setBusySlots(Array.isArray(busy) ? busy : []);
      } catch (e) {
        if (!cancelled) {
          setBusySlots([]);
          addToast({
            type: 'warning',
            title: 'Calendar busy times unavailable',
            message: e.message || 'Showing availability without Google busy blocking.',
          });
        }
      } finally {
        if (!cancelled) setBusyLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, selectedAgentId, selectedDate, addToast]);

  const selectedAgent = useMemo(
    () => agents.find((a) => a.id === selectedAgentId) || null,
    [agents, selectedAgentId],
  );

  const mergeVars = useMemo(() => {
    const agentName = selectedAgent?.full_name || 'Agent';
    let appointmentDate = '—';
    let appointmentTime = '—';
    if (selectedSlotStart) {
      const d = parseISO(selectedSlotStart);
      appointmentDate = format(d, 'EEEE, MMM d');
      appointmentTime = format(d, 'h:mm a');
    }
    return {
      firstName: lead?.first_name?.trim() || 'there',
      agentName,
      appointmentDate,
      appointmentTime,
    };
  }, [lead, selectedAgent, selectedSlotStart]);

  useEffect(() => {
    if (!isOpen || existingAppointment) return;
    const dateStr = formatConfirmationDate(selectedDate, smsLanguage);
    const timeStr = selectedSlotStart ? format(parseISO(selectedSlotStart), 'h:mm a') : '';
    setConfirmationBody(
      buildConfirmationSmsMessage(smsLanguage, {
        firstName: lead?.first_name?.trim() || '',
        agentName: selectedAgent?.full_name || '',
        date: dateStr,
        time: timeStr,
      }),
    );
  }, [
    isOpen,
    existingAppointment,
    smsLanguage,
    selectedAgent,
    selectedDate,
    selectedSlotStart,
    lead?.first_name,
  ]);

  const filteredAgents = useMemo(() => {
    const q = agentSearch.trim().toLowerCase();
    if (!q) return agents;
    return agents.filter((a) => {
      const name = (a.full_name || '').toLowerCase();
      return name.includes(q);
    });
  }, [agents, agentSearch]);

  const filteredGuestUsers = useMemo(() => {
    const guestUserIds = new Set(guests.filter((g) => g.type === 'user' && g.user_id).map((g) => g.user_id));
    const q = guestUserSearch.trim().toLowerCase();
    return agents.filter((a) => {
      if (a.id === selectedAgentId) return false;
      if (guestUserIds.has(a.id)) return false;
      if (!q) return true;
      const name = (a.full_name || '').toLowerCase();
      return name.includes(q);
    });
  }, [agents, guestUserSearch, guests, selectedAgentId]);

  const addCrmGuest = async (user) => {
    if (!user?.id) return;
    if (user.id === selectedAgentId) {
      addToast({ type: 'error', title: 'Cannot add assigned closer as a guest' });
      return;
    }
    if (guests.some((g) => g.type === 'user' && g.user_id === user.id)) {
      addToast({ type: 'error', title: 'Guest already added' });
      return;
    }

    // Directory roster is display-only (id/name/role) — no email/phone.
    // Resolve contact fields from the users row when adding so calendar invites
    // and confirmation emails can include this guest.
    let email = String(user.email ?? '').trim();
    let phone = String(user.phone_number ?? user.phone ?? '').trim();
    if ((!email || !phone) && supabase) {
      try {
        const { data, error } = await supabase
          .from('users')
          .select('email, phone_number')
          .eq('id', user.id)
          .maybeSingle();
        if (!error && data) {
          if (!email) email = String(data.email ?? '').trim();
          if (!phone) phone = String(data.phone_number ?? '').trim();
        }
      } catch (e) {
        console.error('[BookAppointmentModal] guest contact lookup failed', e);
      }
    }

    const guest = {
      type: 'user',
      user_id: user.id,
      name: user.full_name || 'User',
      email,
    };
    if (phone) guest.phone = phone;

    setGuests((prev) => [...prev, guest]);
    setGuestUserDropdownOpen(false);
    setGuestUserSearch('');
  };

  const addExternalGuest = () => {
    const name = externalGuestName.trim();
    if (!name) {
      addToast({ type: 'error', title: 'Guest name required' });
      return;
    }
    setGuests((prev) => [
      ...prev,
      {
        type: 'external',
        name,
        phone: externalGuestPhone.trim(),
        email: externalGuestEmail.trim(),
      },
    ]);
    setExternalGuestFormOpen(false);
    setExternalGuestName('');
    setExternalGuestPhone('');
    setExternalGuestEmail('');
  };

  const removeGuest = (index) => {
    setGuests((prev) => prev.filter((_, i) => i !== index));
  };

  const calendarGuestAttendees = useMemo(
    () =>
      guests
        .filter((g) => g.email && g.email.trim() !== '')
        .map((g) => ({ email: g.email.trim(), displayName: g.name })),
    [guests],
  );

  const slotGrid = useMemo(() => {
    if (!selectedDate || !selectedAgentId) return [];
    const wd = weekdayKeyInTz(selectedDate, timeZone);
    const dayKey = DAY_ORDER.includes(wd) ? wd : 'monday';
    const dayCfg = availability[dayKey] || { enabled: false, start: '09:00', end: '18:00' };
    if (!dayCfg.enabled) return [];

    // Use the full saved weekly window (no hardcoded 8am–8pm grid cap).
    const startMin = parseHm(dayCfg.start);
    const endMin = parseHm(dayCfg.end);
    if (endMin <= startMin) return [];
    const dur = parseInt(durationMinutes, 10) || 30;

    const slots = [];
    for (let m = startMin; m + dur <= endMin; m += 30) {
      const hour = Math.floor(m / 60);
      const minute = m % 60;
      const startUtc = utcInstantForWallTime(selectedDate, hour, minute, timeZone);
      const startIso = startUtc.toISOString();
      const endUtc = new Date(startUtc.getTime() + dur * 60 * 1000);

      let blocked = false;
      for (const b of busySlots) {
        if (!b?.start || !b?.end) continue;
        const b0 = new Date(b.start).getTime();
        const b1 = new Date(b.end).getTime();
        if (intervalsOverlap(startUtc.getTime(), endUtc.getTime(), b0, b1)) {
          blocked = true;
          break;
        }
      }

      const label = format(startUtc, 'h:mm a');
      const selected =
        selectedSlotStart && Math.abs(new Date(selectedSlotStart).getTime() - startUtc.getTime()) < 1000;
      slots.push({ startIso, label, blocked, selected });
    }
    return slots;
  }, [
    selectedDate,
    selectedAgentId,
    timeZone,
    availability,
    busySlots,
    durationMinutes,
    selectedSlotStart,
  ]);

  const handleSave = async () => {
    if (!lead?.id) return;
    if (!title.trim()) {
      addToast({ type: 'error', title: 'Title required' });
      return;
    }
    if (!appointmentType) {
      addToast({ type: 'error', title: 'Appointment type required' });
      return;
    }
    if (!selectedAgentId) {
      addToast({ type: 'error', title: 'Assign a closer' });
      return;
    }
    if (!selectedDate || !selectedSlotStart) {
      addToast({ type: 'error', title: 'Pick a date and time slot' });
      return;
    }
    if (meetingType === 'zoom' && !agentZoomConnected) {
      addToast({
        type: 'error',
        title: 'Zoom not connected',
        message: 'Connect Zoom in Settings or choose another meeting type.',
      });
      return;
    }
    if (meetingType === 'custom_link' && !meetingLocation.trim()) {
      addToast({ type: 'error', title: 'Meeting URL required' });
      return;
    }
    if (meetingType === 'in_person' && !meetingLocation.trim()) {
      addToast({ type: 'error', title: 'Address required' });
      return;
    }

    const scheduledAt = selectedSlotStart;
    const dur = parseInt(durationMinutes, 10) || 30;
    const endDt = new Date(new Date(scheduledAt).getTime() + dur * 60 * 1000).toISOString();
    const reminderMin = Number(agentSnap?.sms_reminder_minutes_before ?? 60);
    const smsReminderScheduledAt =
      sendReminderSms && agentSnap?.sms_reminder_enabled !== false
        ? new Date(new Date(scheduledAt).getTime() - reminderMin * 60 * 1000).toISOString()
        : null;
    const leadName = `${lead.first_name || ''} ${lead.last_name || ''}`.trim();
    const meetingPayload = {
      meeting_type: meetingType,
      meeting_location: meetingType === 'zoom' ? null : meetingLocation.trim() || null,
    };
    const resolvedMeetingLocation =
      meetingType === 'zoom' ? null : meetingLocation.trim() || null;
    const googleCalendarPayloadExtras = {
      lead_name: leadName,
      lead_phone: lead.phone || '',
      appointment_type: appointmentType,
      notes: notes.trim() || '',
      guests: guests.map((g) => ({ name: g.name || '' })).filter((g) => g.name),
      meeting_type: meetingType,
      zoom_join_url: null,
      meeting_location: resolvedMeetingLocation,
    };

    setSaving(true);
    let apptId;
    let zoomJoinUrl = null;
    try {
      if (existingAppointment?.id) {
        const updated = await api.appointments.update(existingAppointment.id, {
          assigned_to: selectedAgentId,
          title: title.trim(),
          notes: notes.trim() || null,
          appointment_type: appointmentType,
          scheduled_at: scheduledAt,
          duration_minutes: dur,
          sms_reminder_scheduled_at: smsReminderScheduledAt,
          guests,
          ...meetingPayload,
        });
        apptId = updated.id;
      } else {
        const newAppointment = await api.appointments.create({
          lead_id: lead.id,
          assigned_to: selectedAgentId,
          title: title.trim(),
          notes: notes.trim() || null,
          appointment_type: appointmentType,
          scheduled_at: scheduledAt,
          duration_minutes: dur,
          status: 'scheduled',
          sms_reminder_scheduled_at: smsReminderScheduledAt,
          guests,
          ...meetingPayload,
        });
        apptId = newAppointment.id;

        // Activity trail note — before Zoom/Google/email so their failures never block it.
        try {
          const booker = me || (await api.auth.getCurrentUser().catch(() => null));
          const activityDate =
            activityDateFromScheduledAt(scheduledAt, timeZone) || selectedDate;
          await api.activityLogs.create(lead.id, {
            activity_type: 'appointment_booked',
            activity_date: activityDate,
            notes: buildAppointmentBookedNotes({
              title: title.trim(),
              appointmentType,
              scheduledAt,
              durationMinutes: dur,
              timeZone,
              meetingType,
              meetingLocation: resolvedMeetingLocation,
              closerName: selectedAgent?.full_name || null,
              guests,
              bookedByName: booker?.full_name || authUser?.email || 'Agent',
            }),
          });
        } catch (logErr) {
          console.error('[BookAppointmentModal] activity_logs insert failed', logErr);
        }

        if (meetingType === 'zoom' && newAppointment.id) {
          const assignedToUserId = newAppointment.assigned_to ?? selectedAgentId;
          try {
            const result = await api.zoom.createMeeting({
              appointmentId: newAppointment.id,
              userId: assignedToUserId,
              topic: `${leadName} - Appointment`,
              startTime: scheduledAt,
              durationMinutes: dur,
            });
            if (result?.zoom_join_url && !result?.error) {
              zoomJoinUrl = result.zoom_join_url;
            } else if (result?.error) {
              console.warn('[BookAppointmentModal] zoom-create-meeting:', result.error, result.details);
            }
          } catch {
            // Fall through to custom-link fallback below
          }

          // Zoom selected but join URL missing (not connected / API failure):
          // fall back to custom_link / mailto so the appointment is never left
          // without a meeting_location.
          if (!zoomJoinUrl) {
            const assignedAgent = agents.find((u) => u.id === assignedToUserId);
            const fallbackLoc = resolveCustomLinkDefault(
              defaultCustomLink,
              assignedAgent?.email || me?.email || authUser?.email,
            );
            if (fallbackLoc) {
              googleCalendarPayloadExtras.meeting_type = 'custom_link';
              googleCalendarPayloadExtras.meeting_location = fallbackLoc;
              googleCalendarPayloadExtras.zoom_join_url = null;
              try {
                await api.appointments.update(newAppointment.id, {
                  meeting_type: 'custom_link',
                  meeting_location: fallbackLoc,
                });
              } catch {
                // Calendar payload still uses the fallback even if DB update fails
              }
              addToast({
                type: 'warning',
                title: 'Zoom meeting not created',
                message:
                  'Used the agent custom meeting link for the calendar event instead. Reconnect Zoom in Settings if this keeps happening.',
              });
            }
          }
        }

        // Send appointment confirmation email — fire and forget
        const { data: { session: emailSession } } = await supabase.auth.refreshSession();
        if (emailSession) {
          fetch(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-appointment-email`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${emailSession.access_token}`,
              },
              body: JSON.stringify({
                appointment_id: newAppointment.id,
                email_type: 'confirmation',
              }),
            }
          ).catch(e => console.error('Confirmation email failed:', e));
        }
      }

      googleCalendarPayloadExtras.zoom_join_url = zoomJoinUrl || null;
      // Prefer extras (may have been switched to custom_link after Zoom failure).
      const calendarMeetingType =
        googleCalendarPayloadExtras.meeting_type || meetingType;
      const calendarMeetingLocation =
        googleCalendarPayloadExtras.meeting_location || meetingLocation;
      if (zoomJoinUrl) {
        googleCalendarPayloadExtras.meeting_location =
          googleCalendarPayloadExtras.meeting_location || zoomJoinUrl;
      }
      const eventLocation = buildCalendarLocationPayload(calendarMeetingType, {
        zoomJoinUrl,
        meetingLocation: calendarMeetingLocation,
      });
      const googleEventBase = {
        summary: title.trim(),
        start_datetime: scheduledAt,
        end_datetime: endDt,
        attendee_email: lead.email || '',
        attendee_name: leadName,
        ...(eventLocation ? { location: eventLocation } : {}),
        ...(calendarGuestAttendees.length > 0 ? { attendees: calendarGuestAttendees } : {}),
      };

      if (!existingAppointment) {
        const snap = await api.agentCalendarSettings.getBookingSnapshot(selectedAgentId).catch(() => null);
        if (snap?.google_connected) {
          try {
            const eventId = await api.googleCalendar.createEvent({
              user_id: selectedAgentId,
              type: 'agent',
              event: googleEventBase,
              ...googleCalendarPayloadExtras,
            });
            if (eventId) {
              await api.appointments.update(apptId, { google_event_id_agent: eventId });
            }
          } catch (e) {
            addToast({
              type: 'warning',
              title: 'Agent Google Calendar',
              message: e.message || 'Event was not added to the agent calendar.',
            });
          }
        }

        try {
          const agencyCal = await api.agentCalendarSettings.getAgencySettings();
          if (agencyCal?.google_connected) {
            const eventIdAgency = await api.googleCalendar.createEvent({
              user_id: selectedAgentId,
              type: 'agency',
              event: googleEventBase,
              ...googleCalendarPayloadExtras,
            });
            if (eventIdAgency) {
              await api.appointments.update(apptId, { google_event_id_agency: eventIdAgency });
            }
          }
        } catch (e) {
          addToast({
            type: 'warning',
            title: 'Agency Google Calendar',
            message: e.message || 'Event was not added to the agency calendar.',
          });
        }
      }

      if (
        !existingAppointment &&
        sendConfirmationSms &&
        lead.phone?.trim() &&
        confirmationBody.trim()
      ) {
        try {
          const user = me || (await api.auth.getCurrentUser());
          const preferredDialer =
            user?.preferred_dialer === 'telnyx' || user?.preferred_dialer === 'twilio'
              ? user.preferred_dialer
              : 'twilio';
          const body = confirmationBody.trim();
          const result = await api.calls.sendSms({
            to: lead.phone.trim(),
            message: body,
            lead_id: lead.id,
            agency_id: lead.agency_id,
            preferredDialer,
          });
          await api.smsMessages.saveOutbound({
            lead_id: lead.id,
            to_number: lead.phone.trim(),
            from_number: result?.from || '',
            body,
            twilio_message_sid: result?.sid || null,
          });
          await api.appointments.update(apptId, {
            sms_confirmation_sent: true,
            sms_confirmation_sent_at: new Date().toISOString(),
          });
        } catch (e) {
          addToast({ type: 'error', title: 'Confirmation SMS failed', message: e.message });
        }
      }

      const prettyWhen = `${mergeVars.appointmentDate} at ${mergeVars.appointmentTime}`;
      addToast({
        type: 'success',
        title: existingAppointment ? 'Appointment updated' : 'Appointment booked',
        message: existingAppointment ? `Saved for ${prettyWhen}` : `Appointment booked for ${prettyWhen}`,
      });
      onSuccess?.();
      onClose?.();
    } catch (e) {
      addToast({ type: 'error', title: 'Could not save appointment', message: e.message });
    } finally {
      setSaving(false);
    }
  };

  if (!lead) return null;

  const modalTitle = existingAppointment ? 'Reschedule appointment' : 'Book appointment';

  return (
    <Modal isOpen={isOpen} onClose={() => !saving && onClose()} title={modalTitle} className="max-w-xl max-h-[90vh] overflow-y-auto">
      <div className="space-y-6 text-sm">
        <section>
          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Appointment details</h4>
          <div className="space-y-3">
            <Input
              label="Title"
              required
              value={title}
              onChange={(e) => {
                titleWasManuallyEdited.current = true;
                setTitle(e.target.value);
              }}
              disabled={saving}
            />
            <Select
              label="Appointment type"
              value={appointmentType}
              onChange={(e) => {
                const newType = e.target.value;
                setAppointmentType(newType);
                const typeOption = appointmentTypeOptions.find((t) => t.value === newType);
                if (!typeOption) return;
                const shouldUpdateTitle =
                  !titleWasManuallyEdited.current ||
                  !title.trim() ||
                  title === lastAutoTitleRef.current;
                if (shouldUpdateTitle) {
                  const newTitle = buildAutoTitle(typeOption.label, lead);
                  setTitle(newTitle);
                  lastAutoTitleRef.current = newTitle;
                }
              }}
              disabled={saving}
              options={appointmentTypeOptions}
            />
            <div>
              <span className="block text-sm font-medium text-gray-700 mb-2">Meeting Type</span>
              <div className="grid grid-cols-3 gap-2">
                {MEETING_TYPE_OPTIONS.map((opt) => {
                  const isZoom = opt.value === 'zoom';
                  const disabled = isZoom && !agentZoomConnected;
                  const active = meetingType === opt.value;
                  return (
                    <div key={opt.value} className="flex flex-col min-w-0">
                      <button
                        type="button"
                        disabled={saving || disabled || agentZoomLoading}
                        onClick={() => {
                          if (disabled) return;
                          setMeetingType(opt.value);
                          if (opt.value === 'zoom') {
                            setMeetingLocation('');
                          } else if (opt.value === 'custom_link') {
                            setMeetingLocation(
                              resolveCustomLinkDefault(
                                defaultCustomLink,
                                authUser?.email || me?.email,
                              ),
                            );
                          } else if (opt.value === 'in_person') {
                            setMeetingLocation(
                              resolveInPersonDefault(
                                authUser?.office_address ?? me?.office_address,
                              ),
                            );
                          }
                        }}
                        className={`w-full px-3 py-2 text-sm font-medium rounded-md border transition-all disabled:opacity-50 ${
                          disabled
                            ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                            : active
                              ? 'bg-[#00AAFF] text-white border-[#00AAFF]'
                              : 'bg-white text-gray-600 border-gray-300 hover:border-[#00AAFF]/40'
                        }`}
                      >
                        {opt.label}
                      </button>
                      {isZoom && !agentZoomConnected && !agentZoomLoading && (
                        <a
                          href="/settings/calendar"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-[#00AAFF] mt-1 hover:text-[#0099e6]"
                        >
                          Connect Zoom in Settings →
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
              {meetingType === 'custom_link' && (
                <div className="mt-3">
                  <Input
                    label="Meeting URL"
                    required
                    value={meetingLocation}
                    onChange={(e) => setMeetingLocation(e.target.value)}
                    disabled={saving}
                    placeholder="https://meet.google.com/..."
                  />
                </div>
              )}
              {meetingType === 'in_person' && (
                <div className="mt-3">
                  <Input
                    label="Address"
                    required
                    value={meetingLocation}
                    onChange={(e) => setMeetingLocation(e.target.value)}
                    disabled={saving}
                    placeholder="123 Main St, City, State"
                  />
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
              <textarea
                className="w-full border border-gray-200 rounded-md p-3 text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none min-h-[72px] resize-y"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={saving}
                placeholder="Internal notes…"
              />
            </div>
          </div>
        </section>

        <section>
          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Assign to</h4>
          {(agentsLoading || directoryLoading) ? (
            <div className="flex items-center gap-2 text-gray-500 py-4">
              <Spinner className="text-primary" />
              Loading agents…
            </div>
          ) : (
            <div className="relative">
              <span className="block text-sm font-medium text-gray-700 mb-1.5">Assigned To (Closer)</span>
              <button
                type="button"
                disabled={saving}
                onClick={() => setAgentDropdownOpen((o) => !o)}
                className="w-full text-left border border-gray-200 rounded-md px-3 py-2 text-sm bg-white hover:bg-gray-50 flex items-center justify-between gap-2"
              >
                <span className="truncate">
                  {selectedAgent ? (
                    <span className="inline-flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-900">
                        {selectedAgent.full_name || 'Agent'}
                      </span>
                      <Badge variant={roleBadgeVariant(selectedAgent.role)} className="capitalize">
                        {roleLabel(selectedAgent.role)}
                      </Badge>
                    </span>
                  ) : (
                    <span className="text-gray-400">Select an agent…</span>
                  )}
                </span>
                <span className="text-gray-400 text-xs shrink-0">▾</span>
              </button>
              {agentDropdownOpen && (
                <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-56 overflow-hidden flex flex-col">
                  <input
                    type="text"
                    className="w-full border-b border-gray-100 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-inset focus:ring-primary"
                    placeholder="Search agents…"
                    value={agentSearch}
                    onChange={(e) => setAgentSearch(e.target.value)}
                    autoFocus
                  />
                  <ul className="overflow-y-auto max-h-44">
                    {filteredAgents.map((a) => (
                      <li key={a.id}>
                        <button
                          type="button"
                          className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center justify-between gap-2"
                          onClick={() => {
                            setSelectedAgentId(a.id);
                            setSelectedSlotStart(null);
                            setAgentDropdownOpen(false);
                            setAgentSearch('');
                          }}
                        >
                          <span className="font-medium text-gray-800 truncate">{a.full_name || 'Agent'}</span>
                          <Badge variant={roleBadgeVariant(a.role)} className="capitalize shrink-0">
                            {roleLabel(a.role)}
                          </Badge>
                        </button>
                      </li>
                    ))}
                    {filteredAgents.length === 0 && (
                      <li className="px-3 py-4 text-gray-500 text-center text-xs">No matching agents</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}
        </section>

        <section>
          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Guests</h4>
          <div className="space-y-3">
            <div className="relative">
              <span className="block text-sm font-medium text-gray-700 mb-1.5">Pick a CRM user</span>
              <button
                type="button"
                disabled={saving || agentsLoading || directoryLoading}
                onClick={() => setGuestUserDropdownOpen((o) => !o)}
                className="w-full text-left border border-gray-200 rounded-md px-3 py-2 text-sm bg-white hover:bg-gray-50 flex items-center justify-between gap-2"
              >
                <span className="text-gray-400">Search agency users…</span>
                <span className="text-gray-400 text-xs shrink-0">▾</span>
              </button>
              {guestUserDropdownOpen && (
                <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-56 overflow-hidden flex flex-col">
                  <input
                    type="text"
                    className="w-full border-b border-gray-100 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-inset focus:ring-primary"
                    placeholder="Search users…"
                    value={guestUserSearch}
                    onChange={(e) => setGuestUserSearch(e.target.value)}
                    autoFocus
                  />
                  <ul className="overflow-y-auto max-h-44">
                    {filteredGuestUsers.map((a) => (
                      <li key={a.id}>
                        <button
                          type="button"
                          className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center justify-between gap-2"
                          onClick={() => void addCrmGuest(a)}
                        >
                          <span className="font-medium text-gray-800 truncate">{a.full_name || 'Agent'}</span>
                          <Badge variant={roleBadgeVariant(a.role)} className="capitalize shrink-0">
                            {roleLabel(a.role)}
                          </Badge>
                        </button>
                      </li>
                    ))}
                    {filteredGuestUsers.length === 0 && (
                      <li className="px-3 py-4 text-gray-500 text-center text-xs">No matching users</li>
                    )}
                  </ul>
                </div>
              )}
            </div>

            {!externalGuestFormOpen ? (
              <button
                type="button"
                disabled={saving}
                onClick={() => setExternalGuestFormOpen(true)}
                className="text-sm font-medium text-[#00AAFF] hover:text-[#0099e6] disabled:opacity-50"
              >
                ＋ Add external guest
              </button>
            ) : (
              <div className="border border-gray-200 rounded-md p-3 space-y-2 bg-gray-50/50">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <input
                    type="text"
                    className="w-full border border-gray-200 rounded-md px-2.5 py-1.5 text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                    placeholder="Name *"
                    value={externalGuestName}
                    onChange={(e) => setExternalGuestName(e.target.value)}
                    disabled={saving}
                  />
                  <input
                    type="text"
                    className="w-full border border-gray-200 rounded-md px-2.5 py-1.5 text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                    placeholder="Phone"
                    value={externalGuestPhone}
                    onChange={(e) => setExternalGuestPhone(e.target.value)}
                    disabled={saving}
                  />
                  <input
                    type="text"
                    className="w-full border border-gray-200 rounded-md px-2.5 py-1.5 text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                    placeholder="Email"
                    value={externalGuestEmail}
                    onChange={(e) => setExternalGuestEmail(e.target.value)}
                    disabled={saving}
                  />
                </div>
                <div className="flex items-center gap-3">
                  <Button size="sm" variant="primary" onClick={addExternalGuest} disabled={saving}>
                    Add
                  </Button>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => {
                      setExternalGuestFormOpen(false);
                      setExternalGuestName('');
                      setExternalGuestPhone('');
                      setExternalGuestEmail('');
                    }}
                    className="text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {guests.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {guests.map((guest, index) => (
                  <span
                    key={`${guest.type}-${guest.user_id || guest.name}-${index}`}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200"
                  >
                    {guest.name}
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => removeGuest(index)}
                      className="text-gray-400 hover:text-gray-700 leading-none disabled:opacity-50"
                      aria-label={`Remove ${guest.name}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </section>

        <section>
          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Schedule</h4>
          <div className="space-y-3">
            <Input
              type="date"
              label="Date"
              min={todayYmdLocal()}
              value={selectedDate}
              onChange={(e) => {
                setSelectedDate(e.target.value);
                setSelectedSlotStart(null);
              }}
              disabled={saving}
            />
            {busyLoading && (
              <p className="text-xs text-gray-500 flex items-center gap-2">
                <Spinner className="h-3 w-3 text-primary" />
                Loading availability…
              </p>
            )}
            {selectedAgentId && selectedDate && (
              <div>
                <span className="block text-sm font-medium text-gray-700 mb-2">Time</span>
                {slotGrid.length === 0 ? (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-3 py-2">
                    No bookable slots on this day for this agent&apos;s availability.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {slotGrid.map((s) => (
                      <button
                        key={s.startIso}
                        type="button"
                        disabled={s.blocked || saving}
                        onClick={() => setSelectedSlotStart(s.startIso)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                          s.selected
                            ? 'text-white border-transparent'
                            : s.blocked
                              ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                              : 'bg-white text-gray-800 border-gray-200 hover:border-primary/50'
                        }`}
                        style={s.selected ? { backgroundColor: '#00AAFF' } : undefined}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <Select
              label="Duration"
              value={durationMinutes}
              onChange={(e) => {
                setDurationMinutes(e.target.value);
                setSelectedSlotStart(null);
              }}
              disabled={saving}
              options={DURATION_OPTIONS}
            />
          </div>
        </section>

        <section>
          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">SMS options</h4>
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-medium text-gray-900">Send confirmation SMS to lead now</p>
                <p className="text-xs text-gray-500 mt-0.5">Only when the lead has a mobile number.</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={sendConfirmationSms}
                disabled={saving || !lead.phone?.trim() || !!existingAppointment}
                onClick={() => setSendConfirmationSms((v) => !v)}
                className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors disabled:opacity-40 ${
                  sendConfirmationSms ? 'bg-primary' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transition translate-x-0.5 ${
                    sendConfirmationSms ? 'translate-x-5' : ''
                  }`}
                />
              </button>
            </div>
            {sendConfirmationSms && lead.phone?.trim() && !existingAppointment && (
              <div>
                <div className="mb-3">
                  <span className="block text-xs font-medium text-gray-600 mb-2">Message language</span>
                  <div className="inline-flex gap-2">
                    {[
                      { value: 'en', label: 'EN' },
                      { value: 'es', label: 'ES' },
                    ].map((opt) => {
                      const active = smsLanguage === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          disabled={saving}
                          onClick={() => setSmsLanguage(opt.value)}
                          className={`px-4 py-2 text-sm font-medium rounded-md border transition-all disabled:opacity-50 ${
                            active
                              ? 'bg-[#00AAFF] text-white border-[#00AAFF]'
                              : 'bg-white text-gray-600 border-gray-300 hover:border-[#00AAFF]/40'
                          }`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Message preview (editable)</label>
                <textarea
                  className="w-full border border-gray-200 rounded-md p-3 text-sm min-h-[100px] resize-y"
                  value={confirmationBody}
                  onChange={(e) => setConfirmationBody(e.target.value)}
                  disabled={saving}
                />
              </div>
            )}

            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-medium text-gray-900">Send reminder SMS before appointment</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {agentSnap
                    ? `Reminder timing: ${formatReminderLabel(agentSnap.sms_reminder_minutes_before)}`
                    : 'Reminder timing comes from the agent’s calendar settings.'}
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={sendReminderSms}
                disabled={saving}
                onClick={() => setSendReminderSms((v) => !v)}
                className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors ${
                  sendReminderSms ? 'bg-primary' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transition translate-x-0.5 ${
                    sendReminderSms ? 'translate-x-5' : ''
                  }`}
                />
              </button>
            </div>
          </div>
        </section>

        <div className="flex justify-end gap-2 pt-4 border-t border-gray-100">
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" onClick={handleSave} disabled={saving} isLoading={saving}>
            {existingAppointment ? 'Save changes' : 'Book appointment'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
